const rawEnv = import.meta.env;

const normalizeString = (value) => String(value ?? "").trim();

const readEnv = (name) => normalizeString(rawEnv[name]);

const readBooleanEnv = (name, defaultValue = false) => {
  const value = readEnv(name).toLowerCase();
  if (!value) return defaultValue;
  return value === "true" || value === "1" || value === "yes";
};

const normalizeUrl = (value) => normalizeString(value).replace(/\/+$/, "");

const parseCsv = (value = "") =>
  normalizeString(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const getHostname = (value = "") => {
  const normalized = normalizeString(value);
  if (!normalized) return "";

  try {
    return new URL(normalized).hostname;
  } catch {
    return normalized.replace(/^https?:\/\//i, "").split("/")[0].trim();
  }
};

const appEnv =
  readEnv("VITE_APP_ENV") ||
  normalizeString(rawEnv.MODE) ||
  (rawEnv.PROD ? "production" : "development");
const siteUrl = normalizeUrl(readEnv("VITE_SITE_URL") || readEnv("VITE_DEPLOY_LINK"));
const deployLink = normalizeUrl(readEnv("VITE_DEPLOY_LINK") || siteUrl);
const allowIndexing = readBooleanEnv("VITE_ALLOW_INDEXING", false);
const indexableHosts = Array.from(
  new Set([
    ...parseCsv(readEnv("VITE_INDEXABLE_HOSTS")),
    ...(allowIndexing && siteUrl ? [getHostname(siteUrl)] : []),
  ]),
);

export const envConfig = Object.freeze({
  appEnv,
  allowIndexing,
  apiBaseUrl: normalizeUrl(readEnv("VITE_BASEURL")),
  deployLink,
  siteUrl,
  indexableHosts,
  isDevelopment: appEnv === "development" || rawEnv.DEV,
  isProduction: appEnv === "production",
  isSandbox: appEnv === "sandbox",
  mode: normalizeString(rawEnv.MODE),
});

export const getEnvValue = (name, defaultValue = "") => {
  const key = name.startsWith("VITE_") ? name : `VITE_${name}`;
  const value = readEnv(key);
  return value || defaultValue;
};

export const validateFrontendEnv = () => {
  if (envConfig.mode === "test") return envConfig;

  const requiredEnvNames = [
    "VITE_APP_ENV",
    "VITE_ALLOW_INDEXING",
    "VITE_BASEURL",
    "VITE_SITE_URL",
    "VITE_DEPLOY_LINK",
  ];
  const missingEnvNames = requiredEnvNames.filter((name) => !readEnv(name));

  if (missingEnvNames.length > 0) {
    throw new Error(
      `[env] Missing required frontend env variables: ${missingEnvNames.join(", ")}`,
    );
  }

  if (envConfig.allowIndexing) {
    const siteHost = getHostname(envConfig.siteUrl);
    if (!envConfig.indexableHosts.includes(siteHost)) {
      throw new Error(
        `[env] ${siteHost} must be included in VITE_INDEXABLE_HOSTS when VITE_ALLOW_INDEXING=true.`,
      );
    }
  }

  if (!envConfig.isProduction && envConfig.allowIndexing) {
    console.warn(
      "[env] VITE_ALLOW_INDEXING=true outside production. Verify this deployment should be indexable.",
    );
  }

  return envConfig;
};
